import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/siteData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'usage-guide');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.category === activeCategory);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 91.com` : '分类页面 - 91.com',
    description: currentCategory?.description || '',
    url: `https://91.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91.com',
      url: 'https://91.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Category Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="mx-auto px-4 md:px-6 max-w-[1200px]">
              <div className="mb-4">
                <Breadcrumb items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '分类' },
                ]} />
              </div>
              {currentCategory && (
                <div className="flex items-center gap-3 mt-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${currentCategory.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{currentCategory.name}</h1>
                    <p className="text-sm text-foreground-600 mt-1">{currentCategory.description}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Category Tabs & Articles */}
          <section className="py-10 md:py-14">
            <div className="mx-auto px-4 md:px-6 max-w-[1200px]">
              {/* Category Switcher */}
              <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon}`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Articles Grid */}
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <Link key={article.id} to={`/article/${article.slug}`} className="group block p-5 rounded-lg bg-background-100 hover:bg-background-100 transition-colors cursor-pointer">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-foreground-500">{article.date}</span>
                        <span className="text-xs text-foreground-500">·</span>
                        <span className="text-xs text-foreground-500 flex items-center gap-1">
                          <i className="ri-time-line"></i> {article.readTime}
                        </span>
                      </div>
                      <h2 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}